import { LogOut, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { CommanServices } from '@/services/commanServices';
import { useDispatch } from 'react-redux';
import { disconnectSocket } from '../Redux/Action/socketActions';
import { useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { beginLogoutTransition } from '../auth/logoutTransition';
import { resetMarketplaceGuideForNextLogin } from '../auth/marketplaceGuideSession';

const LogoutModal = ({ onClose, isSaaS = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const titleId = useId();
  const descriptionId = useId();

  const handleLogout = () => {
    if (isLoggingOut) return;
    // Keep the modal covering the protected shell until the public login
    // route commits. Closing it after clearing the session briefly exposes
    // the tenant fallback shell because the old Layout is still mounted.
    setIsLoggingOut(true);
    beginLogoutTransition({ isSaaS });
    if (isSaaS) resetMarketplaceGuideForNextLogin();
    // ensure socket disconnects cleanly before logging out
    dispatch(disconnectSocket());
    CommanServices.logout();
    navigate('/login', { replace: true });
    // Keep this covering modal mounted. Login resets its Redux flag only
    // after the public route commits, making the route transition atomic.
  };

  return createPortal(
    <div
      className={`fixed inset-0 z-[2000] flex items-center justify-center p-4 ${isSaaS ? 'dms-saas-shell bg-black/75 backdrop-blur-sm' : 'bg-black/50'}`}
      data-theme={isSaaS ? 'saas-dark' : 'tenant-light'}
      data-modal-overlay="true"
      data-testid="logout-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      onClick={() => { if (!isLoggingOut) onClose(); }}
    >
      {/* Popup Modal */}
      <div
        className={`relative ${isSaaS
          ? 'w-full max-w-sm rounded-[16px] border border-white/[0.12] bg-[linear-gradient(145deg,#10183a,#0c1230)] p-8 text-white shadow-[0_28px_80px_rgba(0,0,0,0.55)]'
          : 'w-full max-w-sm rounded-xl bg-white p-8 shadow-2xl'} transform transition-all`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          disabled={isLoggingOut}
          className={`absolute right-4 top-4 rounded-md p-1 transition-colors disabled:cursor-wait disabled:opacity-50 ${isSaaS ? 'text-[#5f6890] hover:bg-white/[0.06] hover:text-white' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'}`}
          aria-label="Close logout confirmation"
        >
          <X size={24} />
        </button>

        <div className="mb-6 text-center">
          <div className={`mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full ${isSaaS ? 'border border-[#ef3937]/25 bg-[#ef3937]/10' : 'bg-red-100'}`}>
            <LogOut className={isSaaS ? 'text-[#ef3937]' : 'text-red-600'} size={24} />
          </div>
          <h2 id={titleId} className={`mb-2 text-xl font-bold ${isSaaS ? 'text-white' : 'text-gray-800'}`}>Confirm Logout</h2>
          <p id={descriptionId} className={isSaaS ? 'text-[#aab2cc]' : 'text-gray-600'}>Are you sure you want to logout?</p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={isLoggingOut}
            className={`flex-1 rounded-lg border-2 px-4 py-2 font-medium transition-colors disabled:cursor-wait disabled:opacity-60 ${isSaaS
              ? 'border-white/[0.14] bg-white/[0.03] text-[#d7dcef] hover:bg-white/[0.07] hover:text-white'
              : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
          >
            Cancel
          </button>
          <button
            onClick={handleLogout}
            disabled={isLoggingOut}
            className={`flex-1 rounded-lg px-4 py-2 font-medium text-white transition-colors disabled:cursor-wait disabled:opacity-75 ${isSaaS ? 'bg-[#ef3937] hover:bg-[#d92f2d]' : 'bg-red-500 hover:bg-red-600'}`}
          >
            {isLoggingOut ? 'Signing out…' : 'Logout'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default LogoutModal;
